import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useLanguage } from '@/contexts/LanguageContext';

interface TrendPoint {
  label: string;
  applied: number;
  hired: number;
  rejected: number;
}

export const TrendChart = ({ data }: { data: TrendPoint[] }) => {
  const { t } = useLanguage();

  if (data.length === 0) {
    return (
      <Card className="border-border/50">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">{t('dashboard.applicationTrend')}</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.noDataForPeriod')}</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-sm font-medium">{t('dashboard.applicationTrend')}</CardTitle>
          <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="text-[11px] text-muted-foreground">{t('dashboard.applied')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-success" />
              <span className="text-[11px] text-muted-foreground">{t('dashboard.hired')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-destructive/80" />
              <span className="text-[11px] text-muted-foreground">{t('dashboard.rejected')}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="gradApplied" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradHired" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradRejected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(0, 72%, 51%)" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="hsl(0, 72%, 51%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 4%, 90%)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'hsl(240, 4%, 46%)' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'hsl(240, 4%, 46%)' }} width={30} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ payload, label }) => {
                  if (!payload?.length) return null;
                  return (
                    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
                      <p className="font-medium mb-1">{label}</p>
                      {payload.map(p => {
                        const labelKey =
                          p.dataKey === 'applied'
                            ? 'dashboard.applied'
                            : p.dataKey === 'hired'
                              ? 'dashboard.hired'
                              : 'dashboard.rejected';
                        return (
                          <p key={p.dataKey as string} className="text-muted-foreground">
                            {t(labelKey)}: {p.value}
                          </p>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="applied"
                stroke="hsl(221, 83%, 53%)"
                strokeWidth={2}
                fill="url(#gradApplied)"
                dot={{ r: 3, fill: 'hsl(221, 83%, 53%)', strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(0, 0%, 100%)' }}
              />
              <Area
                type="monotone"
                dataKey="hired"
                stroke="hsl(142, 71%, 45%)"
                strokeWidth={2}
                fill="url(#gradHired)"
                dot={{ r: 3, fill: 'hsl(142, 71%, 45%)', strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(0, 0%, 100%)' }}
              />
              <Area
                type="monotone"
                dataKey="rejected"
                stroke="hsl(0, 72%, 51%)"
                strokeWidth={2}
                fill="url(#gradRejected)"
                dot={{ r: 3, fill: 'hsl(0, 72%, 51%)', strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(0, 0%, 100%)' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
